import { useEffect, useState } from 'react';
import type { JSX, KeyboardEvent } from 'react';
import { Badge, Box, Button, Grid, IconButton, Tooltip } from '@mui/material';
import {
  ColumnsPanelTrigger,
  DataGrid,
  FilterPanelTrigger,
  GridColDef,
  GridFilterListIcon,
  GridRenderCellParams,
  GridRowSelectionModel,
  GridViewColumnIcon,
  Toolbar,
  ToolbarButton,
  gridFilterModelSelector,
  useGridApiContext,
  useGridSelector,
} from '@mui/x-data-grid';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import SaveIcon from '@mui/icons-material/Save';
import CancelIcon from '@mui/icons-material/Cancel';
import { t } from 'i18next';
import { useSelector } from 'react-redux';
import {
  deleteFields,
  EToolbarButton,
  foreignKeysForTable,
  RecordLength,
  TableHeaders,
} from '../../data/Constants';
import {
  TableSearchBar,
  TableRecordPicker,
  TablePaginationRow,
  renderNoRecordsMessage,
  renderCell,
  renderEditCell,
  renderButtonSection,
  computeDisableDelete,
  createBaseColumn,
  sharedDataGridProps,
  showConfirmationDialog,
} from './Helper/DataTableHelper';
import type { AppState } from '../../store';
import type {
  IActionableDataTableProps,
  IActionableToolbarProps,
  IDataTableRow,
  IStateData,
  ITableHeaders,
  IHeaderActionHandlers,
  IHeaderActionMap,
} from '../../../types';

// #region Toolbar

/** Toolbar rendered inside the DataGrid — search, record picker, column/filter toggles, and action buttons. */
function ActionableToolbar({
  searchValue,
  searchFieldsMessage,
  recordsPerPage,
  headerActions,
  disableDelete,
  onSearch,
  onRecordsChange,
  onRefresh,
}: Readonly<IActionableToolbarProps>): JSX.Element {
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);
  const filterCount =
    filterModel?.items?.filter(
      (item) => item.value !== undefined && item.value !== null && item.value !== ''
    ).length || 0;

  return (
    <Toolbar>
      <Grid container spacing={1} className="w-100" sx={{ alignItems: 'center' }}>
        <Grid size="grow" className="d-flex align-center">
          <TableSearchBar
            value={searchValue}
            placeholder={searchFieldsMessage}
            onChange={onSearch}
          />
          &emsp;
          <TableRecordPicker value={recordsPerPage} onChange={onRecordsChange} />
          &emsp;
          <ColumnsPanelTrigger
            render={
              <ToolbarButton
                render={<Button size="small" startIcon={<GridViewColumnIcon fontSize="small" />} />}
              >
                {t('labels.columns')}
              </ToolbarButton>
            }
          />
          &emsp;
          <FilterPanelTrigger>
            <Button
              size="small"
              startIcon={
                <Badge variant="dot" color="primary" invisible={filterCount === 0}>
                  <GridFilterListIcon />
                </Badge>
              }
            >
              {t('labels.filters')}
            </Button>
          </FilterPanelTrigger>
          &emsp;
          <Button
            color="primary"
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={onRefresh}
            className="refresh-button common-btn"
          >
            {t('button.refresh')}
          </Button>
        </Grid>
        <Grid className="d-flex justify-end">
          {renderButtonSection({ headerActions, disableDelete })}
        </Grid>
      </Grid>
    </Toolbar>
  );
}

// #endregion

// #region ActionableDataTable

/**
 * Standalone data table for LOV and LOV Options maintenance.
 *
 * Handles its own data loading, pagination, search, and inline editing.
 * Business logic (save / delete API calls) is delegated to the parent via callbacks.
 *
 * @param props.type - Redux state key and TableHeaders key (e.g. EDataTableType.Lov)
 * @param props.moduleType - Redux module key (e.g. EModuleType.Datamaintainance)
 * @param props.loadData - Called to fetch a page of data
 * @param props.headerActions - Toolbar actions keyed by button type, each with its own handler
 * @param props.rowActions - Row-level actions, e.g. `{ save: { onClick: ... } }`
 * @param props.readOnly - When true, the edit button is disabled
 * @param props.onCellClick - Called when a Link cell is clicked (e.g. to open LOV Options)
 */
export const ActionableDataTable = ({
  type,
  moduleType,
  headerActions,
  forceLoadOnMount = false,
  loadData,
  rowActions,
  readOnly,
  onCellClick,
}: IActionableDataTableProps): JSX.Element => {
  // ── Pagination / search state ──────────────────────────────────────────────
  const [searchValue, setSearchValue] = useState('');
  const [recordsPerPage, setRecordsPerPage] = useState(RecordLength[0]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [selectionModel, setSelectionModel] = useState<GridRowSelectionModel>({
    type: 'include',
    ids: new Set(),
  });

  // ── Inline-edit state ──────────────────────────────────────────────────────
  const [editingRowId, setEditingRowId] = useState<number | string | null>(null);
  const [editValues, setEditValues] = useState<Record<string, string>>({});
  const [originalValues, setOriginalValues] = useState<Record<string, string>>({});

  // ── Redux data ─────────────────────────────────────────────────────────────
  const tableData = useSelector(
    (state: AppState) =>
      (state as unknown as Record<string, Record<string, IStateData>>)[moduleType]?.[type]
  );

  const currentPageData: IDataTableRow[] = tableData?.data?.[page] ?? [];
  const tableHeaders: ITableHeaders[] = TableHeaders[type].main;
  const uniqueKey = foreignKeysForTable[type];
  const editableHeaders = tableHeaders.filter((h) => h.isEditable);

  // ── Effects ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (forceLoadOnMount || !tableData?.data?.[1]) {
      loadData({ page: 1, limit: RecordLength[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!tableData) return;
    setSearchValue(tableData.searchKey || '');
    setRecordsPerPage(tableData.recordsPerPage || RecordLength[0]);
    setPageCount(Math.ceil(tableData.totalCount / tableData.recordsPerPage) || 1);
  }, [tableData]);

  const gridKey = `${searchValue}-${recordsPerPage}-${page}`;

  // ── Toolbar handlers ───────────────────────────────────────────────────────
  /** Trims the search string, resets to page 1, and fires a new data request. */
  const handleSearch = (newVal: string): void => {
    const trimmed = newVal?.trim() || '';
    setSearchValue(trimmed);
    loadData({ page: 1, limit: recordsPerPage, searchCode: trimmed });
    setPage(1);
  };

  /** Updates the page-size limit, resets to page 1, and reloads data. */
  const handleRecordsChange = (records: number): void => {
    setRecordsPerPage(records);
    loadData({ page: 1, limit: records, searchCode: searchValue });
    setPage(1);
  };

  /** Re-fetches the current page without changing any filter or pagination state. */
  const handleRefresh = (): void => {
    loadData({ page, limit: recordsPerPage, searchCode: searchValue });
  };

  /** Navigates to the requested page, fetching from the API only when that page is not yet cached. */
  const handlePaginationChange = (_: React.ChangeEvent<unknown>, value: number): void => {
    setPage(value);
    if (!tableData?.data?.[value]) {
      loadData({ page: value, limit: recordsPerPage, searchCode: searchValue });
    }
  };

  /**
   * Binds each requested header action to a no-argument handler.
   *
   * Delete resolves the unique keys of the selected rows and clears the selection afterwards;
   * every other action simply forwards to the parent's handler. Empty when the parent passes
   * no header actions, which renders no buttons.
   */
  const headerActionHandlers: IHeaderActionHandlers = Object.fromEntries(
    (
      Object.entries(headerActions ?? {}) as [EToolbarButton, IHeaderActionMap[EToolbarButton]][]
    ).map(([name, config]) => {
      if (name === EToolbarButton.Delete) {
        return [
          name,
          (): void => {
            const isExcludeMode = selectionModel.type === 'exclude';
            const selectedCodes = currentPageData
              .filter((row) => {
                const rowId = row.id ?? row.code ?? '';
                return isExcludeMode
                  ? !selectionModel.ids.has(rowId)
                  : selectionModel.ids.has(rowId);
              })
              .map((row) => row[uniqueKey] as string);

            if (config?.showDialog) {
              showConfirmationDialog(
                type,
                selectedCodes,
                () => {
                  config.onClick(selectedCodes);
                  setSelectionModel({ type: 'include', ids: new Set() });
                },
                config.dialogMessage
              );
            } else {
              config?.onClick(selectedCodes);
              setSelectionModel({ type: 'include', ids: new Set() });
            }
          },
        ];
      }
      return [name, () => config?.onClick([])];
    })
  );

  // ── Inline-edit handlers ───────────────────────────────────────────────────
  /** Returns the stable string identifier for a row, preferring `id` then `code`. */
  const getRowId = (row: IDataTableRow): string => row.id ?? row.code ?? '';

  /** Snapshots the row's editable fields into state and marks that row as being edited. */
  const handleEdit = (row: IDataTableRow): void => {
    const vals: Record<string, string> = {};
    editableHeaders.forEach((h) => {
      vals[h.field] = String(row[h.field] ?? '');
    });
    setEditingRowId(getRowId(row));
    setEditValues({ ...vals });
    setOriginalValues({ ...vals });
  };

  /** Commits the current edit values via the parent callback and clears editing state. */
  const handleSave = (id: number | string, row: IDataTableRow): void => {
    rowActions?.save?.onClick?.(id, editValues, row);
    setEditingRowId(null);
    setEditValues({});
    setOriginalValues({});
  };

  /** Discards any pending edits and exits inline-edit mode. */
  const handleCancel = (): void => {
    setEditingRowId(null);
    setEditValues({});
    setOriginalValues({});
  };

  /** Updates a single editable field in the in-progress edit buffer. */
  const handleFieldChange = (field: string, value: string): void => {
    setEditValues((prev) => ({ ...prev, [field]: value }));
  };

  /** Saves on Enter (when changes exist) or cancels on Escape. */
  const handleKeyDown = (e: KeyboardEvent, rowId: number | string, row: IDataTableRow): void => {
    if (e.key === 'Enter' && hasChanged) handleSave(rowId, row);
    if (e.key === 'Escape') handleCancel();
  };

  const hasChanged =
    editingRowId !== null &&
    editableHeaders.some((h) => editValues[h.field] !== originalValues[h.field]);

  const isDisabledByInactive = editableHeaders
    .filter((h) => h.field === 'isActive')
    .some((h) => editValues[h.field] === 'false');

  const deleteConfig = (
    deleteFields as unknown as Record<string, { field: string; inverseCheck: boolean }>
  )[type];

  const disableDelete = computeDisableDelete(
    currentPageData,
    selectionModel,
    deleteConfig?.field ?? '',
    deleteConfig?.inverseCheck ?? false
  );

  // ── Column definitions ─────────────────────────────────────────────────────
  const baseColumns: GridColDef[] = tableHeaders.map((header) => ({
    ...createBaseColumn(header, (params) => renderCell(params, header, { onCellClick })),
    renderCell: (params: GridRenderCellParams) => {
      const editableHeader = editableHeaders.find((h) => h.field === header.field);
      if (!editableHeader || editingRowId !== getRowId(params.row)) {
        return renderCell(params, header, { onCellClick });
      }
      // isActive is only editable when the row is currently inactive
      if (header.field === 'isActive' && originalValues['isActive'] === 'true') {
        return renderCell(params, header, { onCellClick });
      }
      return renderEditCell(
        header.field,
        editableHeader.type,
        getRowId(params.row),
        params.row as IDataTableRow,
        editValues,
        isDisabledByInactive,
        { onFieldChange: handleFieldChange, onKeyDown: handleKeyDown }
      );
    },
  }));

  const columns: GridColDef[] = [
    ...baseColumns,
    {
      field: '__actions',
      headerName: t('labels.actions'),
      width: 120,
      sortable: false,
      filterable: false,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params: GridRenderCellParams) => {
        const rowId = getRowId(params.row);
        const isEditing = editingRowId === rowId;
        return (
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <Tooltip title={isEditing ? t('button.cancel') : t('labels.edit')}>
              <span>
                <IconButton
                  onClick={() =>
                    isEditing ? handleCancel() : handleEdit(params.row as IDataTableRow)
                  }
                  disabled={readOnly && !isEditing}
                  sx={isEditing ? { color: 'error.main' } : { color: 'primary.main' }}
                >
                  {isEditing ? <CancelIcon /> : <EditIcon />}
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title={t('button.save')}>
              <span>
                <IconButton
                  onClick={() => handleSave(rowId, params.row as IDataTableRow)}
                  disabled={!isEditing || !hasChanged}
                  color="primary"
                >
                  <SaveIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        );
      },
    } as GridColDef,
  ];

  // ── Render ─────────────────────────────────────────────────────────────────
  const searchFieldsMessage = tableHeaders
    .filter((col) => col.searchable)
    .map((col) => t('labels.' + col.field))
    .join(', ');

  return (
    <Box className="maintenanceTable">
      <DataGrid
        key={gridKey}
        rows={currentPageData}
        columns={columns}
        {...sharedDataGridProps}
        getRowId={(row) => getRowId(row as IDataTableRow)}
        checkboxSelection
        onRowSelectionModelChange={setSelectionModel}
        rowSelectionModel={selectionModel}
        slots={{
          toolbar: () =>
            ActionableToolbar({
              searchValue,
              searchFieldsMessage,
              recordsPerPage,
              headerActions: headerActionHandlers,
              disableDelete,
              onSearch: handleSearch,
              onRecordsChange: handleRecordsChange,
              onRefresh: handleRefresh,
            }),
          noRowsOverlay: () => renderNoRecordsMessage(searchValue),
        }}
        slotProps={{ columnsManagement: { disableShowHideToggle: true } }}
        sx={{
          '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
          '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': {
            outline: 'none',
          },
          '& .MuiDataGrid-cell': { py: 0, alignItems: 'center', display: 'flex' },
        }}
      />

      <br />
      <TablePaginationRow
        count={pageCount}
        page={page}
        show={currentPageData.length > 0}
        onChange={handlePaginationChange}
      />
    </Box>
  );
};

// #endregion
